import React from "react";
import {
  UsersIcon,
  GlobeAltIcon,
  HeartIcon,
  ShieldCheckIcon,
  SparklesIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";

import Hero from "../components/Hero";
import heroImg from "../assests/images/hero.jpg";
import aboutImg from "../assests/images/aboutUs.jpg";
import CTASection from "../components/CTASection";

import { Helmet } from "react-helmet-async";

export default function AboutUs() {
  const features = [
    {
      icon: UsersIcon,
      title: "Trusted by Travelers",
      desc: "Thousands of travelers rely on us to discover comfortable stays and great hotel deals.",
    },
    {
      icon: GlobeAltIcon,
      title: "Prime Locations",
      desc: "Discover hotels close to Dubai's beaches, attractions, shopping and business districts.",
    },
    {
      icon: HeartIcon,
      title: "Guest First",
      desc: "We focus on making every part of your hotel search and booking experience simple.",
    },
    {
      icon: ShieldCheckIcon,
      title: "Secure & Reliable",
      desc: "Book with confidence through a platform designed around trust and convenience.",
    },
  ];

  return (
    <>
      <Helmet>
        <title>About Us | Jumeira Discount Hotels</title>

        <meta
          name="description"
          content="Learn more about Jumeira Discount Hotels, your trusted platform for affordable hotel bookings and the best hotel deals and stays in Dubai."
        />

        <link
          rel="canonical"
          href="https://jumeiradiscounthotels.com/about-us"
        />

        <meta
          property="og:title"
          content="About Us | Jumeira Discount Hotels"
        />

        <meta
          property="og:description"
          content="Learn more about Jumeira Discount Hotels, your trusted platform for affordable hotel bookings and the best hotel deals and stays in Dubai."
        />

        <meta
          property="og:url"
          content="https://jumeiradiscounthotels.com/about-us"
        />

        <meta
          property="og:type"
          content="website"
        />

        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content="About Us | Jumeira Discount Hotels"
        />

        <meta
          name="twitter:description"
          content="Learn more about Jumeira Discount Hotels, your trusted platform for affordable hotel bookings and the best hotel deals and stays in Dubai."
        />

        <meta
          name="robots"
          content="index, follow"
        />
      </Helmet>

      <div className="bg-white">

        {/* =====================================================
                    HERO
                ====================================================== */}

        <Hero
          variant="inner"
          title="About Us"
          subtitle="Helping travelers discover better stays, better value, and unforgettable experiences in Dubai."
          backgroundImage={heroImg}
        />


        {/* =====================================================
                    OUR STORY
                ====================================================== */}

        <section className="relative py-16 sm:py-20 lg:py-28 overflow-hidden">

          {/* Decorative */}
          <div className="absolute -top-32 -right-32 w-80 h-80 bg-[#D4A83F]/10 rounded-full blur-3xl"></div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">

              {/* Image */}
              <div className="relative">

                <div className="
                                    relative
                                    rounded-[30px]
                                    overflow-hidden
                                    shadow-[0_25px_70px_rgba(15,23,42,0.15)]
                                ">

                  <img
                    src={aboutImg}
                    alt="About Beach Hotels Dubai"
                    className="
                                            w-full
                                            h-[320px]
                                            sm:h-[420px]
                                            lg:h-[540px]
                                            object-cover
                                            hover:scale-105
                                            transition-transform
                                            duration-700
                                        "
                  />

                  <div className="
                                        absolute
                                        inset-0
                                        bg-gradient-to-t
                                        from-[#002B45]/40
                                        via-transparent
                                        to-transparent
                                    "></div>

                </div>

                {/* Experience Badge */}
                <div className="
                                    absolute
                                    -bottom-7
                                    left-5
                                    sm:left-8
                                    bg-white
                                    rounded-2xl
                                    shadow-[0_15px_40px_rgba(15,23,42,0.15)]
                                    px-6
                                    py-5
                                    min-w-[180px]
                                ">

                  <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
                    Experience
                  </p>

                  <div className="flex items-end gap-2 mt-1">

                    <span className="text-3xl font-extrabold text-[#002B45]">
                      25+
                    </span>

                    <span className="text-sm text-slate-500 mb-1">
                      Years
                    </span>

                  </div>

                </div>

                {/* Gold Accent */}
                <div className="
                                    absolute
                                    -top-5
                                    -left-5
                                    w-20
                                    h-20
                                    border-t-4
                                    border-l-4
                                    border-[#D4A83F]
                                    rounded-tl-3xl
                                "></div>

              </div>


              {/* Content */}
              <div className="lg:pl-4">

                <span className="
                                    inline-flex
                                    items-center
                                    px-4
                                    py-2
                                    rounded-full
                                    bg-[#D4A83F]/10
                                    text-[#9A7418]
                                    text-xs
                                    sm:text-sm
                                    font-bold
                                    uppercase
                                    tracking-wide
                                ">
                  Our Story
                </span>

                <h2 className="
                                    text-3xl
                                    sm:text-4xl
                                    lg:text-5xl
                                    font-extrabold
                                    text-[#002B45]
                                    leading-tight
                                    mt-5
                                ">
                  Making Every Stay
                  <span className="text-[#D4A83F]">
                    {" "}Worth Remembering
                  </span>
                </h2>

                <p className="
                                    text-slate-600
                                    leading-7
                                    mt-6
                                ">
                  We started with a simple mission: make finding
                  the right hotel easy, fast, and reliable.
                  Whether you're visiting Dubai for business,
                  relaxation, shopping, or a memorable getaway,
                  we help you discover accommodation that fits
                  your needs.
                </p>

                <p className="
                                    text-slate-600
                                    leading-7
                                    mt-4
                                ">
                  Through our growing network of hotels and
                  travel partners, we bring together comfortable
                  stays, competitive rates, and a seamless
                  booking experience.
                </p>


                {/* Highlights */}
                <div className="grid grid-cols-3 gap-3 sm:gap-5 mt-8">

                  <div className="
                                        rounded-2xl
                                        bg-slate-50
                                        border
                                        border-slate-100
                                        p-4
                                    ">
                    <h3 className="text-2xl font-extrabold text-[#002B45]">
                      500+
                    </h3>

                    <p className="text-xs text-slate-500 mt-1">
                      Hotels
                    </p>
                  </div>

                  <div className="
                                        rounded-2xl
                                        bg-slate-50
                                        border
                                        border-slate-100
                                        p-4
                                    ">
                    <h3 className="text-2xl font-extrabold text-[#002B45]">
                      10K+
                    </h3>

                    <p className="text-xs text-slate-500 mt-1">
                      Travelers
                    </p>
                  </div>

                  <div className="
                                        rounded-2xl
                                        bg-slate-50
                                        border
                                        border-slate-100
                                        p-4
                                    ">
                    <h3 className="text-2xl font-extrabold text-[#002B45]">
                      24/7
                    </h3>

                    <p className="text-xs text-slate-500 mt-1">
                      Support
                    </p>
                  </div>

                </div>

              </div>

            </div>

          </div>
        </section>


        {/* =====================================================
                    MISSION + VISION
                ====================================================== */}

        <section className="relative py-16 sm:py-20 lg:py-24 bg-slate-50 overflow-hidden">

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

            {/* Heading */}
            <div className="max-w-2xl mb-12">

              <span className="
                                inline-flex
                                px-4
                                py-2
                                rounded-full
                                bg-[#D4A83F]/10
                                text-[#9A7418]
                                text-xs
                                sm:text-sm
                                font-bold
                                uppercase
                                tracking-wide
                            ">
                What Drives Us
              </span>

              <h2 className="
                                text-3xl
                                sm:text-4xl
                                font-extrabold
                                text-[#002B45]
                                mt-4
                            ">
                Built Around Better Travel
              </h2>

            </div>


            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

              {/* Mission */}
              <div className="
                                relative
                                overflow-hidden
                                rounded-[28px]
                                bg-[#002B45]
                                p-7
                                sm:p-10
                                text-white
                                min-h-[300px]
                                flex
                                flex-col
                                justify-between
                                shadow-[0_20px_50px_rgba(0,43,69,0.15)]
                            ">

                <div className="
                                    absolute
                                    -right-20
                                    -top-20
                                    w-60
                                    h-60
                                    rounded-full
                                    bg-[#D4A83F]/15
                                    blur-3xl
                                "></div>

                <div className="relative">

                  <div className="
                                        w-14
                                        h-14
                                        rounded-2xl
                                        bg-[#D4A83F]
                                        text-[#002B45]
                                        flex
                                        items-center
                                        justify-center
                                    ">
                    <SparklesIcon className="w-7 h-7" />
                  </div>

                  <p className="
                                        text-xs
                                        uppercase
                                        tracking-[0.2em]
                                        text-[#D4A83F]
                                        font-bold
                                        mt-7
                                    ">
                    Our Mission
                  </p>

                  <h3 className="
                                        text-2xl
                                        sm:text-3xl
                                        font-bold
                                        mt-2 text-white
                                    ">
                    Simplifying the way you travel.
                  </h3>

                  <p className="
                                        text-slate-300
                                        leading-7
                                        mt-4
                                        max-w-lg
                                    ">
                    To simplify travel by connecting people
                    with great hotels, competitive prices,
                    and a booking experience they can trust.
                  </p>

                </div>

                <div className="
                                    absolute
                                    bottom-0
                                    right-0
                                    w-32
                                    h-1
                                    bg-[#D4A83F]
                                "></div>

              </div>


              {/* Vision */}
              <div className="
                                relative
                                overflow-hidden
                                rounded-[28px]
                                bg-white
                                border
                                border-slate-200
                                p-7
                                sm:p-10
                                min-h-[300px]
                                flex
                                flex-col
                                justify-between
                                shadow-[0_15px_40px_rgba(15,23,42,0.06)]
                            ">

                <div>

                  <div className="
                                        w-14
                                        h-14
                                        rounded-2xl
                                        bg-[#D4A83F]/10
                                        text-[#9A7418]
                                        flex
                                        items-center
                                        justify-center
                                    ">
                    <GlobeAltIcon className="w-7 h-7" />
                  </div>

                  <p className="
                                        text-xs
                                        uppercase
                                        tracking-[0.2em]
                                        text-[#9A7418]
                                        font-bold
                                        mt-7
                                    ">
                    Our Vision
                  </p>

                  <h3 className="
                                        text-2xl
                                        sm:text-3xl
                                        font-bold
                                        text-[#002B45]
                                        mt-2
                                    ">
                    A better way to discover Dubai.
                  </h3>

                  <p className="
                                        text-slate-500
                                        leading-7
                                        mt-4
                                        max-w-lg
                                    ">
                    To become a trusted platform for hotel
                    booking and travel experiences, helping
                    travelers find memorable stays wherever
                    their journey takes them.
                  </p>

                </div>

                <div className="
                                    absolute
                                    bottom-0
                                    right-0
                                    w-32
                                    h-1
                                    bg-[#D4A83F]
                                "></div>

              </div>

            </div>

          </div>
        </section>


        {/* =====================================================
                    WHY CHOOSE US
                ====================================================== */}

        <section className="
                    relative
                    py-16
                    sm:py-20
                    lg:py-24
                    bg-[#002B45]
                    overflow-hidden
                ">

          {/* Decorative */}
          <div className="
                        absolute
                        -top-40
                        -right-40
                        w-[450px]
                        h-[450px]
                        rounded-full
                        bg-[#D4A83F]/10
                        blur-3xl
                    "></div>

          <div className="
                        absolute
                        -bottom-40
                        -left-40
                        w-[400px]
                        h-[400px]
                        rounded-full
                        bg-[#174B5F]/40
                        blur-3xl
                    "></div>


          <div className="
                        relative
                        max-w-7xl
                        mx-auto
                        px-4
                        sm:px-6
                        lg:px-8
                    ">

            <div className="text-center max-w-2xl mx-auto mb-12">

              <span className="
                                inline-flex
                                px-4
                                py-2
                                rounded-full
                                bg-white/10
                                border
                                border-white/10
                                text-[#D4A83F]
                                text-xs
                                sm:text-sm
                                font-bold
                                uppercase
                                tracking-wide
                            ">
                Why Choose Us
              </span>

              <h2 className="
                                text-3xl
                                sm:text-4xl
                                font-extrabold
                                !text-white
                                mt-5
                            ">
                Travel With Confidence
              </h2>

              <p className="
                                text-slate-300
                                mt-4
                                leading-7
                            ">
                Everything we do is focused on making your hotel
                search easier, more transparent, and more enjoyable.
              </p>

            </div>


            <div className="
                            grid
                            grid-cols-1
                            sm:grid-cols-2
                            lg:grid-cols-4
                            gap-5
                        ">

              {features.map((item, index) => {
                const Icon = item.icon;

                return (
                  <div
                    key={index}
                    className="
                                            group
                                            relative
                                            bg-white/5
                                            backdrop-blur-sm
                                            border
                                            border-white/10
                                            rounded-[24px]
                                            p-7
                                            hover:bg-white/10
                                            hover:-translate-y-2
                                            transition-all
                                            duration-500
                                        "
                  >

                    {/* Number */}
                    <span className="
                                            absolute
                                            top-5
                                            right-5
                                            text-xs
                                            font-bold
                                            text-white/20
                                        ">
                      0{index + 1}
                    </span>

                    {/* Icon */}
                    <div className="
                                            w-14
                                            h-14
                                            rounded-2xl
                                            bg-[#D4A83F]/10
                                            text-[#D4A83F]
                                            flex
                                            items-center
                                            justify-center
                                            group-hover:bg-[#D4A83F]
                                            group-hover:text-[#002B45]
                                            transition-all
                                            duration-300
                                        ">
                      <Icon className="w-7 h-7" />
                    </div>

                    <h3 className="
                                            text-lg
                                            font-bold
                                            text-white
                                            mt-6
                                        ">
                      {item.title}
                    </h3>

                    <p className="
                                            text-sm
                                            text-slate-400
                                            leading-6
                                            mt-3
                                        ">
                      {item.desc}
                    </p>

                    <div className="
                                            flex
                                            items-center
                                            gap-2
                                            mt-5
                                            text-xs
                                            font-semibold
                                            text-[#D4A83F]
                                        ">
                      <CheckCircleIcon className="w-4 h-4" />
                      Travel with confidence
                    </div>

                  </div>
                );
              })}

            </div>

          </div>
        </section>


        {/* =====================================================
                    FINAL CTA
                ====================================================== */}

        <CTASection />

      </div>
    </>
  );
}